import { z } from "zod";

export const DTOPageEnum = z.enum([
    "welcome",
    "quoteStart",
    "quoteResults",
    "personalInfo",
    "invalid-link",
    "beneficiary",
    "secondaryAddressee",
    "healthStatement",
    "paymentInfo",
    "review",
    "consent",
    "processing",
    "approved",
    "declined",
    "unableToFinish",
    "oopsSorry",
    "sessionExpired",
    "checkBack",
    "welcomeBack",
]);

export type DTOPages = z.infer<typeof DTOPageEnum>;

export const PagesEnumSchema = z.enum([
    // Legacy MLTA page names
    "addressee",
    "approved",
    "beneficiaries",
    "check-back",
    "consent",
    "declined",
    "health-statement",
    "oops-sorry",
    "invalid-link",
    "payment-info",
    "personal-info",
    "processing",
    "quote-results",
    "quote-start",
    "review",
    "session-expired",
    "unable-to-finish",
    "welcome-back",
    "welcome",
    // DTO page names
    "invitation",
    "quote",
    "application-contact",
    "application-contact-information",
    "application-statement-of-health",
    "application-coverage-replacement",
    "application-policy-beneficiary",
    "application-secondary-addressee",
    "application-payment",
    "application-review",
    "decision-success",
    "decision-denied",
    "decision-needs-closer-look",
    // GIWL-only decision outcomes
    "decision-coverage-limit-exceeded",
    "decision-reviewing-application",
    "something-went-wrong",
    "confirm-your-identity",
    "uncompleted-application-expired",
    "authentication-failed",
    // Old DTO page names (backward compatibility)
    "direct-mail-term",
    "direct-mail-term-invitation",
    "direct-mail-term-quote",
    "direct-mail-term-session-expired",
    "direct-mail-term-welcome-back",
    "direct-mail-term-application-contact",
    "direct-mail-term-application-contact-information",
    "direct-mail-term-application-statement-of-health",
    "direct-mail-term-application-coverage-replacement",
    "direct-mail-term-application-policy-beneficiary",
    "direct-mail-term-application-secondary-addressee",
    "direct-mail-term-application-payment",
    "direct-mail-term-application-review",
    "direct-mail-term-decision-success",
    "direct-mail-term-decision-denied",
    "direct-mail-term-decision-needs-closer-look",
    "direct-mail-term-something-went-wrong",
]);

export type Pages = z.infer<typeof PagesEnumSchema>;

export const PathEnumSchema = z.enum([
    "/", // Welcome page or root redirect
    "/welcome-back",
    "/check-back",
    "/system-error",
    "/session-expired",
    "/invalid-link",
    // Legacy MLTA routes
    "/quote",
    "/quote/results",
    "/app/personal-info",
    "/app/beneficiaries",
    "/app/secondary-addressee",
    "/app/health-statement",
    "/app/payment-info",
    "/app/review",
    "/app/consent",
    "/app/processing",
    "/results/approved",
    "/results/declined",
    "/results/unable-to-finish",
    // DTO routes (clean URLs)
    "/invitation",
    "/application/contact",
    "/application/contact#contact-information",
    "/application/contact#statement-of-health",
    "/application/contact#coverage-replacement",
    "/application/contact#policy-beneficiary",
    "/application/contact#secondary-addressee",
    "/application/payment",
    "/application/review",
    "/decision/success",
    "/decision/denied",
    "/decision/needs-closer-look",
    // GIWL-only decision outcomes
    "/decision/coverage-limit-exceeded",
    "/decision/reviewing-application",
    "/something-went-wrong",
    "/confirm-your-identity",
    "/uncompleted-application-expired",
    "/authentication-failed",
    // Old DTO URLs (backward compatibility - redirects to clean URLs)
    "/direct-mail-term",
    "/direct-mail-term/invitation",
    "/direct-mail-term/quote",
    "/direct-mail-term/session-expired",
    "/direct-mail-term/welcome-back",
    "/direct-mail-term/application/contact",
    "/direct-mail-term/application/contact#contact-information",
    "/direct-mail-term/application/contact#statement-of-health",
    "/direct-mail-term/application/contact#coverage-replacement",
    "/direct-mail-term/application/contact#policy-beneficiary",
    "/direct-mail-term/application/contact#secondary-addressee",
    "/direct-mail-term/application/payment",
    "/direct-mail-term/application/review",
    "/direct-mail-term/decision/success",
    "/direct-mail-term/decision/denied",
    "/direct-mail-term/decision/needs-closer-look",
    "/direct-mail-term/something-went-wrong",
]);

export type Paths = z.infer<typeof PathEnumSchema>;

// Card order for application validation
export const APPLICATION_CARD_ORDER = [
    "contact-information",
    "statement-of-health",
    "coverage-replacement",
    "policy-beneficiary",
    "secondary-addressee",
] as const;

export type ApplicationCardId = (typeof APPLICATION_CARD_ORDER)[number];

const pathToPageNameMapping: { [K in Paths]: Pages } = {
    "/": "welcome",
    "/welcome-back": "welcome-back",
    "/check-back": "check-back",
    "/system-error": "oops-sorry",
    "/invalid-link": "invalid-link",
    "/session-expired": "session-expired",
    "/quote": "quote-start",
    "/quote/results": "quote-results",
    "/app/personal-info": "personal-info",
    "/app/beneficiaries": "beneficiaries",
    "/app/secondary-addressee": "addressee",
    "/app/health-statement": "health-statement",
    "/app/payment-info": "payment-info",
    "/app/review": "review",
    "/app/consent": "consent",
    "/app/processing": "processing",
    "/results/approved": "approved",
    "/results/declined": "declined",
    "/results/unable-to-finish": "unable-to-finish",
    // DTO routes
    "/invitation": "invitation",
    "/application/contact": "application-contact",
    "/application/contact#contact-information": "application-contact-information",
    "/application/contact#statement-of-health": "application-statement-of-health",
    "/application/contact#coverage-replacement": "application-coverage-replacement",
    "/application/contact#policy-beneficiary": "application-policy-beneficiary",
    "/application/contact#secondary-addressee": "application-secondary-addressee",
    "/application/payment": "application-payment",
    "/application/review": "application-review",
    "/decision/success": "decision-success",
    "/decision/denied": "decision-denied",
    "/decision/needs-closer-look": "decision-needs-closer-look",
    "/decision/coverage-limit-exceeded": "decision-coverage-limit-exceeded",
    "/decision/reviewing-application": "decision-reviewing-application",
    "/something-went-wrong": "something-went-wrong",
    "/confirm-your-identity": "confirm-your-identity",
    "/uncompleted-application-expired": "uncompleted-application-expired",
    "/authentication-failed": "authentication-failed",
    // Old DTO routes (backward compatibility)
    "/direct-mail-term": "direct-mail-term",
    "/direct-mail-term/invitation": "direct-mail-term-invitation",
    "/direct-mail-term/quote": "direct-mail-term-quote",
    "/direct-mail-term/session-expired": "direct-mail-term-session-expired",
    "/direct-mail-term/welcome-back": "direct-mail-term-welcome-back",
    "/direct-mail-term/application/contact": "direct-mail-term-application-contact",
    "/direct-mail-term/application/contact#contact-information": "direct-mail-term-application-contact-information",
    "/direct-mail-term/application/contact#statement-of-health": "direct-mail-term-application-statement-of-health",
    "/direct-mail-term/application/contact#coverage-replacement": "direct-mail-term-application-coverage-replacement",
    "/direct-mail-term/application/contact#policy-beneficiary": "direct-mail-term-application-policy-beneficiary",
    "/direct-mail-term/application/contact#secondary-addressee": "direct-mail-term-application-secondary-addressee",
    "/direct-mail-term/application/payment": "direct-mail-term-application-payment",
    "/direct-mail-term/application/review": "direct-mail-term-application-review",
    "/direct-mail-term/decision/success": "direct-mail-term-decision-success",
    "/direct-mail-term/decision/denied": "direct-mail-term-decision-denied",
    "/direct-mail-term/decision/needs-closer-look": "direct-mail-term-decision-needs-closer-look",
    "/direct-mail-term/something-went-wrong": "direct-mail-term-something-went-wrong",
};

export function toPageNameFromPathname(pathname: string): Pages | undefined {
    // Prepend forward slash to relative pathname values which are missing it
    // as it turns out that useNavigate's navigate method causes this
    if (pathname && !pathname.startsWith("/")) {
        pathname = "/" + pathname;
    }

    // Remove trailing slash (except for root path "/")
    if (pathname.length > 1 && pathname.endsWith("/")) {
        pathname = pathname.slice(0, -1);
    }

    const parseResult = PathEnumSchema.safeParse(pathname);

    if (!parseResult.success) {
        console.warn(parseResult.error.message);
        return undefined;
    }

    const path = parseResult.data;

    return pathToPageNameMapping[path];
}

export function isFinalPage(pathname: Paths) {
    switch (toPageNameFromPathname(pathname)) {
        case "approved":
        case "declined":
        case "unable-to-finish":
        case "session-expired":
            return true;
        default:
            return false;
    }
}

export function toDTOPageFromPageName(pageName: Pages | undefined): DTOPages {
    if (!pageName) return "welcome";

    switch (pageName) {
        // DTO clean URL page names
        case "invitation":
            return "welcome";
        case "quote":
            return "quoteStart";
        case "application-contact":
        case "application-contact-information":
        case "application-statement-of-health":
        case "application-coverage-replacement":
        case "application-policy-beneficiary":
        case "application-secondary-addressee":
            return "personalInfo";
        case "application-payment":
            return "paymentInfo";
        case "application-review":
            return "review";
        case "decision-success":
            return "approved";
        case "decision-denied":
            return "declined";
        case "decision-needs-closer-look":
            return "unableToFinish";
        case "decision-coverage-limit-exceeded":
            return "declined";
        case "decision-reviewing-application":
            return "unableToFinish";
        case "something-went-wrong":
            return "oopsSorry";
        case "welcome-back":
            return "welcomeBack";
        case "session-expired":
            return "sessionExpired";

        // Old DTO page names (backward compatibility)
        case "direct-mail-term":
        case "direct-mail-term-invitation":
            return "welcome";
        case "direct-mail-term-quote":
            return "quoteStart";
        case "direct-mail-term-welcome-back":
            return "welcomeBack";
        case "direct-mail-term-session-expired":
            return "sessionExpired";
        case "direct-mail-term-something-went-wrong":
            return "oopsSorry";
        case "direct-mail-term-application-contact":
        case "direct-mail-term-application-contact-information":
        case "direct-mail-term-application-statement-of-health":
        case "direct-mail-term-application-coverage-replacement":
        case "direct-mail-term-application-policy-beneficiary":
        case "direct-mail-term-application-secondary-addressee":
            return "personalInfo";
        case "direct-mail-term-application-payment":
            return "paymentInfo";
        case "direct-mail-term-application-review":
            return "review";
        case "direct-mail-term-decision-success":
            return "approved";
        case "direct-mail-term-decision-denied":
            return "declined";
        case "direct-mail-term-decision-needs-closer-look":
            return "unableToFinish";

        // Legacy MLTA page names
        case "addressee":
            return "secondaryAddressee";
        case "beneficiaries":
            return "beneficiary";
        case "health-statement":
            return "healthStatement";
        case "payment-info":
            return "paymentInfo";
        case "personal-info":
            return "personalInfo";
        case "quote-start":
            return "quoteStart";
        case "quote-results":
            return "quoteResults";
        case "unable-to-finish":
            return "unableToFinish";
        case "oops-sorry":
            return "oopsSorry";
        case "check-back":
            return "checkBack";

        default:
            return pageName as DTOPages;
    }
}
